import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { RequiringActionControllerApi } from "../../../../../../../services";
import {
  DGT_Pagination,
  getConfigObject,
  PaginatedContent,
  useBaseContent,
} from "../../../../../../../utils/utils";

interface Props {
  pagination: DGT_Pagination;
  startDate?: string;
  endDate?: string;
  patientUuid?: string;
  xTENANTID?: string;
  enabled?: boolean;
}

/**
 * Paginated variant of the provider "Requiring Actions" list, used to drive
 * infinite scroll on the patient chart timeline. Hits the `/paginated`
 * endpoint (page + size) and returns the response body as-is; the consumer
 * normalises the shape (grouped date-map content + page metadata) and regroups
 * by day.
 *
 * @author [Rajkumar Rathod]
 */
export const useRequiringActionPaginated = (
  params: Props
): useBaseContent<any> => {
  const {
    pagination,
    startDate,
    endDate,
    patientUuid,
    xTENANTID,
    enabled = true,
  } = params;

  return useQuery({
    queryKey: [
      "requiring-action-paginated",
      pagination,
      startDate,
      endDate,
      patientUuid,
      xTENANTID,
    ],
    refetchOnWindowFocus: false,
    enabled: enabled && !!patientUuid,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      await new RequiringActionControllerApi(getConfigObject())
        .getProviderRequiringActionsForPatient(
          patientUuid!,
          startDate,
          endDate,
          pagination.page,
          pagination.pageSize,
          xTENANTID
        )
        .then((res) => res?.data as unknown as PaginatedContent<any>),
  });
};
